<script setup lang="ts">
import { MtdButton, MtdModalMobile, MtdTypography } from '@paygine/mtd-uikit'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import type { Steps } from '@/shared/constants'

import { userDataModel } from '@/entities/user-data'
import { useSubscription } from '@/features/subscription'
import { AttentionIcon, WarningSmallIcon } from '@/shared/icons'
import { dateFromTimestamp, formatPhone } from '@/shared/lib/formatting'
import { VLabel, VPayMethod } from '@/shared/ui'
import { TransportManagement } from '@/widgets/transport-management'

const emit = defineEmits<{
    goto: [Steps]
}>()

const store = userDataModel.useModel()
const { userData } = storeToRefs(store)
const { setUserData } = store

const { isLoading, removePayMethod, updateStatusSubscription } = useSubscription()

const isDeleteModalOpen = ref(false)
const isChangeModalOpen = ref(false)
const selectedBank = ref<null | userDataModel.Bank>(null)

const paymentMethods = computed(() => userData.value?.payment_methods ?? { cards: null, sbp: [] })

const hasActiveMethod = computed(() =>
    paymentMethods.value?.sbp?.some((method) => method.is_active)
)

function addPayMethod() {
    emit('goto', 'select-pay-method')
}

function changeBank(bank: userDataModel.Bank) {
    if (bank.is_active) return

    selectedBank.value = bank
    isChangeModalOpen.value = true
}

function deleteBank(bank: userDataModel.Bank) {
    selectedBank.value = bank
    isDeleteModalOpen.value = true
}

async function deletePayMethodHandler() {
    if (!selectedBank.value?.id) return

    const res = await removePayMethod(selectedBank.value?.id)

    if (!res) {
        console.error('Ошибка при удалении способа оплаты')
    }

    isDeleteModalOpen.value = false
}

async function updateSubcription(methodId: number) {
    if (!methodId) return

    const res = await updateStatusSubscription(methodId, true)

    if (res && paymentMethods.value && userData.value) {
        setUserData({
            ...userData.value,
            payment_methods: {
                ...paymentMethods.value,
                sbp: paymentMethods.value?.sbp.map((bank) => ({
                    ...bank,
                    is_active: methodId === bank.id,
                })),
            },
        })
    }
    isChangeModalOpen.value = false
}
</script>

<template>
    <MtdTypography
        variant="header-5"
        class="default-layout__title"
        tag="h1"
        >{{ formatPhone(userData?.phone || '') }}
    </MtdTypography>

    <form
        action=""
        class="sub-management__form mt-24"
    >
        <div class="sub-management__pay-methods">
            <div class="sub-management__pay-methods-header">
                <MtdTypography
                    variant="textbody-1-semi"
                    tag="h2"
                    >Способ оплаты
                </MtdTypography>

                <MtdButton
                    v-if="userData?.payment_methods?.sbp?.length"
                    variant="main-flat"
                    type="button"
                    class="sub-management__pay-methods-header-button"
                    size="xs"
                    @click="addPayMethod"
                    >Добавить</MtdButton
                >
            </div>

            <VLabel
                v-if="!hasActiveMethod && paymentMethods.sbp?.length"
                variant="warning"
                class="mt-16"
            >
                <template #icon>
                    <WarningSmallIcon />
                </template>

                <template #text>
                    Выберите способ оплаты, <br />
                    чтобы активировать подписку
                </template>
            </VLabel>

            <template v-if="userData?.payment_methods?.sbp?.length">
                <VPayMethod
                    v-for="bank in userData?.payment_methods?.sbp"
                    :key="bank.id"
                    class="mt-16"
                    :can-edit="false"
                    :can-delete="true"
                    :clickable="true"
                    :active="bank.is_active && userData?.payment_methods?.sbp?.length > 1"
                    :bank="{
                        bankName: bank.bank_name,
                        id: bank.id,
                        logoUrl: bank.bank_logo,
                        subtitle: bank.subscr_service_name,
                    }"
                    @pay-method:change="changeBank(bank)"
                    @pay-method:delete="deleteBank(bank)"
                />
            </template>

            <template v-else>
                <VLabel
                    variant="warning"
                    class="mt-16"
                >
                    <template #icon>
                        <WarningSmallIcon />
                    </template>

                    <template #text>
                        Добавьте способ оплаты, <br />
                        чтобы активировать подписку
                    </template>
                </VLabel>

                <MtdButton
                    class="mt-16"
                    type="button"
                    variant="main"
                    @click="addPayMethod"
                >
                    Добавить
                </MtdButton>
            </template>
        </div>

        <TransportManagement
            :transport-list="userData?.grn_list"
            class="mt-24"
        />

        <MtdTypography
            variant="textbody-3"
            tag="p"
            class="mt-24 text-secondary"
            >Подписка подключена {{ dateFromTimestamp(userData?.creation_date) }}</MtdTypography
        >

        <MtdModalMobile v-model="isDeleteModalOpen">
            <template #content>
                <AttentionIcon />

                <MtdTypography
                    variant="header-5"
                    class="default-layout__title mt-16"
                    tag="h1"
                    >Удалить способ оплаты?
                </MtdTypography>

                <VPayMethod
                    v-if="selectedBank"
                    class="mt-16"
                    :bank="{
                        bankName: selectedBank.bank_name,
                        id: selectedBank.id,
                        logoUrl: selectedBank.bank_logo,
                        subtitle: selectedBank.subscr_service_name,
                    }"
                    :can-edit="false"
                />

                <div class="button-container">
                    <MtdButton
                        type="button"
                        variant="main-secondary"
                        @click="isDeleteModalOpen = false"
                        >Назад</MtdButton
                    >
                    <MtdButton
                        type="button"
                        variant="danger-secondary"
                        :is-loading="isLoading"
                        @click="deletePayMethodHandler"
                        >Удалить</MtdButton
                    >
                </div>
            </template>
        </MtdModalMobile>

        <MtdModalMobile v-model="isChangeModalOpen">
            <template #content>
                <MtdTypography
                    variant="header-5"
                    class="default-layout__title mt-16"
                    tag="h1"
                    >Установить новый способ оплаты?
                </MtdTypography>

                <VPayMethod
                    v-if="selectedBank"
                    class="mt-16"
                    :bank="{
                        bankName: selectedBank.bank_name,
                        id: selectedBank.id,
                        logoUrl: selectedBank.bank_logo,
                        subtitle: selectedBank.subscr_service_name,
                    }"
                    :can-edit="false"
                />

                <MtdButton
                    type="button"
                    class="mt-20"
                    variant="main"
                    :is-loading="isLoading"
                    @click="updateSubcription(selectedBank?.id || 0)"
                    >Установить</MtdButton
                >
            </template>
        </MtdModalMobile>
    </form>
</template>

<style src="./VSubscriptionManagement.scss" scoped lang="scss"></style>
